import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const FiX = FiIcons.FiX;

/**
 * Composant Modal réutilisable avec accessibilité complète.
 *
 * Props :
 * - isOpen        : boolean
 * - onClose       : function
 * - title         : string | ReactNode
 * - children      : ReactNode
 * - size          : 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' (default: 'md')
 * - showCloseButton : boolean (default: true)
 * - footer        : ReactNode (optionnel)
 * - className     : string (optionnel, classes supplémentaires sur le container)
 * - initialFocusRef : ref d'un élément à focusser à l'ouverture
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  footer,
  className = '',
  initialFocusRef
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef(null);
  const closeButtonRef = useRef(null);
  const contentRef = useRef(null);

  // Tailles responsives
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    full: 'max-w-full mx-4'
  };

  // Fermeture avec Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus management + scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      } else if (contentRef.current) {
        contentRef.current.focus();
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, initialFocusRef]);

  // Focus trap simplifié
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const content = contentRef.current;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = Array.from(
        content.querySelectorAll(
          'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], input[type="date"], input[type="time"], input[type="number"], select, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.disabled && el.offsetParent !== null);

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    content.addEventListener('keydown', handleTabKey);
    return () => content.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={handleOverlayClick}
          role="presentation"
        >
          <motion.div
            ref={contentRef}
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={`
              bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full
              ${sizeClasses[size] || sizeClasses.md}
              max-h-[90vh] flex flex-col
              ${className}
            `}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 shrink-0">
              <h2
                id="modal-title"
                className="text-xl font-semibold text-slate-800 dark:text-white truncate pr-4"
              >
                {title}
              </h2>
              {showCloseButton && (
                <button
                  ref={closeButtonRef}
                  onClick={onClose}
                  className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  aria-label={t('common.close') || 'Fermer'}
                  type="button"
                >
                  <SafeIcon icon={FiX} className="text-lg" />
                </button>
              )}
            </div>

            {/* Body */}
            <div className="px-6 py-5 overflow-y-auto flex-1">
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Modal;
